// studioPages — the Studio's material as an outline and as pages (pen
// "Studio", Decided 29 Oct; #315). Pure: the cards on the project's canvas
// (studioCards.ts), the page shown (the desk's path, studioDesk.ts), the
// author's folds and what waits for the author are projected into
//  - the ROOT: the whole content. When the project holds exactly one course
//    (every new project starts with one, named after it), the root page IS
//    that course; otherwise it is the project, holding its courses, modules
//    and loose files. Sources are not material (LM-1): they live in the
//    right zone's Sources tab, never here, not even when a container
//    lists one (its data stays; it is just not shown as a part);
//  - the OUTLINE: first the pages about the material (Info, Learning
//    objectives), then the
//    root and every container's parts at any depth the library holds, each
//    row with the desk commands that open it;
//  - the PAGE shown: the root, a container (its parts as page links), a
//    document, or a page about the project (Info, Sources); with its
//    breadcrumb (every level above it, shown in the top bar: topCrumbsOf)
//    and, for a document, the way back to its container and on to the next
//    part.
// A page the library no longer holds falls back to the root (never a blank
// middle). A kind this build does not know shows as a plain file.

export type {
    AboutRow,
    LinkNote,
    LinkStatus,
    OutlineMark,
    OutlineRow,
    PageCrumb,
    PageLink,
    PageNodeKind,
    Waiting,
};
export {
    aboutRows,
    escapeCommands,
    outlineRows,
    rootOf,
    SUMMARY_AFTER,
    studioPage,
    topCrumbsOf,
};

import { exhaustSwitchCase } from "@bilda/utils";
import type { ContainerKind } from "../../app/containerFacts.js";
import type {
    ContainerCard,
    ContainerPart,
    StudioCard,
} from "../Studio/studioCards.js";
import {
    type DeskAbout,
    type DeskCommand,
    type DeskDocument,
    type DeskState,
    deskBinder,
    deskDocument,
    inFocus,
    openAboutCommands,
    openCommands,
    openPartCommands,
} from "../Studio/studioDesk.js";
import { type DocState, docStateOf, isQuiz } from "../StudioDesk/deskFacts.js";

// What a row or a link is, for its icon and its kind word.
type PageNodeKind = ContainerKind | "page" | "quiz" | "source" | "file";

// What waits for the author, by resource id: suggestions to check (proposals
// open, an AI draft not reviewed). Projected from the project's issues.
type Waiting = ReadonlyMap<string, number>;

// What a link is, in one quiet note: a container's parts, a document's state.
type LinkNote =
    | { readonly type: "doc"; readonly state: DocState; readonly ai: boolean }
    | { readonly type: "parts"; readonly count: number };

// The one word where only one fits (a page's meta, the outline's dot):
// suggestions waiting win over the note.
type LinkStatus =
    | { readonly type: "suggested"; readonly count: number }
    | LinkNote;

type PageLink = {
    readonly id: string;
    readonly title: string;
    readonly kind: PageNodeKind;
    // What it should contain (a planned document's brief), if said.
    readonly brief?: string;
    readonly status: LinkStatus;
    readonly note: LinkNote;
    // Suggestions waiting in it (and in its parts).
    readonly suggested: number;
    // It is the assistant's plan, not kept yet: the "Suggested" mark.
    readonly suggestion: boolean;
    // Its parts (a container), for the outline's rows below it.
    readonly links: readonly PageLink[];
    readonly open: readonly DeskCommand[];
    // Where it lies in the container it is listed in (absent: loose on the
    // canvas, no container to move it in).
    readonly holder?: {
        readonly container: ContainerCard;
        readonly at: number;
        readonly part: ContainerPart;
    };
};

type StudioRoot =
    // The project's one course: the root page is it.
    | { readonly type: "course"; readonly card: ContainerCard }
    // Anything else: the project itself.
    | { readonly type: "project" };

// One level above the page shown; `open` goes back to it.
type PageCrumb = {
    readonly id: string;
    readonly title: string;
    readonly open: readonly DeskCommand[];
};

type StudioPage =
    | {
          readonly type: "root";
          readonly title: string;
          // The course it is, if any (plan a module, readiness).
          readonly container: ContainerCard | undefined;
          readonly links: readonly PageLink[];
      }
    | {
          readonly type: "container";
          readonly id: string;
          readonly title: string;
          readonly container: ContainerCard;
          readonly crumbs: readonly PageCrumb[];
          readonly links: readonly PageLink[];
          // How many things wait inside it.
          readonly suggested: number;
      }
    | {
          readonly type: "document";
          readonly id: string;
          readonly doc: DeskDocument;
          readonly kind: PageNodeKind;
          readonly crumbs: readonly PageCrumb[];
          readonly suggested: number;
          // Back: the container (or root) it lies in.
          readonly back: PageCrumb | undefined;
          // Next: the part after it there.
          readonly next: PageCrumb | undefined;
      }
    // A page about the project (its Info, its Sources).
    | { readonly type: "about"; readonly id: DeskAbout };

const rootOf = (cards: readonly StudioCard[]): StudioRoot => {
    const courses = cards.filter(
        (c): c is ContainerCard =>
            c.type === "container" && c.kind === "course",
    );
    const [only, ...more] = courses;
    return only !== undefined && more.length === 0
        ? { type: "course", card: only }
        : { type: "project" };
};

const kindOfPart = (part: ContainerPart): PageNodeKind => {
    switch (part.kind) {
        case "module":
            return "module";
        case "material":
            return isQuiz(part) ? "quiz" : "page";
        case "source":
            return "source";
        case "other":
            return "file";
        default:
            return exhaustSwitchCase(part.kind);
    }
};

const kindOfCard = (card: StudioCard): PageNodeKind => {
    switch (card.type) {
        case "container":
            return card.kind;
        case "material":
            return isQuiz(card) ? "quiz" : "page";
        case "source":
            return "source";
        case "generic":
            return "file";
        default:
            return exhaustSwitchCase(card);
    }
};

const sumWaiting = (waiting: Waiting, links: readonly PageLink[]): number =>
    links.reduce(
        (n, l) => n + (waiting.get(l.id) ?? 0) + sumWaiting(waiting, l.links),
        0,
    );

// What a link says: its note (what it is) and, apart, how many suggestions
// wait in it; `status` is the one word where only one fits (suggestions
// first).
const linkFacts = (
    waiting: Waiting,
    links: readonly PageLink[],
    own: {
        readonly id: string;
        readonly container: boolean;
        readonly state: DocState;
        readonly ai: boolean;
    },
): Pick<PageLink, "status" | "note" | "suggested"> => {
    const suggested = (waiting.get(own.id) ?? 0) + sumWaiting(waiting, links);
    const note: LinkNote = own.container
        ? { type: "parts", count: links.length }
        : { type: "doc", state: own.state, ai: own.ai };
    return {
        status: suggested > 0 ? { type: "suggested", count: suggested } : note,
        note,
        suggested,
    };
};

// A container's parts as links; `opener` gives the commands that open one.
// A source a container lists is no part of the material (LM-1): it stays in
// the container's data (its place counts for moves) but is never a link.
const partLinks = (
    parts: readonly ContainerPart[],
    opener: (id: string) => readonly DeskCommand[],
    waiting: Waiting,
    listedIn: ContainerCard | undefined,
): readonly PageLink[] =>
    parts.flatMap((part, at): readonly PageLink[] => {
        if (part.kind === "source") return [];
        const links =
            part.kind === "module"
                ? partLinks(part.parts, opener, waiting, undefined)
                : [];
        return [
            {
                id: part.id,
                title: part.title,
                kind: kindOfPart(part),
                brief: part.document?.brief,
                suggestion: part.suggestion === true,
                ...linkFacts(waiting, links, {
                    id: part.id,
                    container: part.kind === "module",
                    state: docStateOf(part),
                    ai: part.aiAuthored,
                }),
                links,
                open: opener(part.id),
                holder:
                    listedIn === undefined
                        ? undefined
                        : { container: listedIn, at, part },
            },
        ];
    });

// A card loose on the canvas as a link (a container with its parts).
const cardLink = (card: StudioCard, waiting: Waiting): PageLink => {
    const links =
        card.type === "container"
            ? partLinks(
                  card.parts,
                  (id) => openCommands(card, id),
                  waiting,
                  card,
              )
            : [];
    const state =
        card.type === "material"
            ? docStateOf({
                  kind: "material",
                  planned: card.planned,
                  document: card.document,
              })
            : card.type === "source"
              ? "source"
              : "unknown";
    return {
        id: card.id,
        title: card.title,
        kind: kindOfCard(card),
        brief: card.type === "material" ? card.document?.brief : undefined,
        suggestion: card.type === "material" && card.suggestion === true,
        ...linkFacts(waiting, links, {
            id: card.id,
            container: card.type === "container",
            state,
            ai: card.type === "material" && card.aiAuthored,
        }),
        links,
        open: openCommands(card, undefined),
    };
};

// The root's links: the course's parts and the rest of the material (not the
// course itself, and no loose sources: they are in the Sources tab).
const rootLinks = (
    cards: readonly StudioCard[],
    root: StudioRoot,
    waiting: Waiting,
): readonly PageLink[] => {
    const loose = cards
        .filter((c) => c.type !== "source")
        .filter((c) => root.type !== "course" || c.id !== root.card.id)
        .map((c) => cardLink(c, waiting));
    if (root.type === "project") return loose;
    const { card } = root;
    return [
        ...partLinks(card.parts, (id) => openCommands(card, id), waiting, card),
        ...loose,
    ];
};

// --- The outline ---------------------------------------------------------------

type OutlineRow = {
    // Unique in the outline (a document can lie in two modules).
    readonly key: string;
    readonly id: string;
    readonly title: string;
    readonly kind: PageNodeKind | "root";
    readonly depth: number;
    readonly fold: "open" | "folded" | "leaf";
    // The page shown is this row.
    readonly current: boolean;
    // What it says waits for the author.
    readonly mark: OutlineMark;
    // The assistant's plan, not kept yet: the "Suggested" mark.
    readonly suggestion: boolean;
    readonly open: readonly DeskCommand[];
};

// What an outline row says waits for the author (pen Q76, "Needs you in
// the outline"): the one or two parts that wait each keep a dot; past that,
// their container's row shows one neutral count and the parts show nothing.
// A folded container stands in for its hidden parts the same way.
type OutlineMark =
    | { readonly type: "none" }
    | { readonly type: "dot" }
    | { readonly type: "count"; readonly count: number };

// More waiting parts than this in one container: its row counts them.
const SUMMARY_AFTER = 2;

const NO_MARK: OutlineMark = { type: "none" };
const DOT: OutlineMark = { type: "dot" };

const isContainerLink = (link: PageLink): boolean =>
    link.kind === "module" || link.kind === "course";

// A container's own parts (not its sub-containers) that wait.
const waitingParts = (links: readonly PageLink[]): number =>
    links.filter((l) => !isContainerLink(l) && l.suggested > 0).length;

// Every part below a container, at any depth, that waits. O(n) in the parts.
const waitingBelow = (links: readonly PageLink[]): number =>
    links.reduce(
        (n, l) =>
            n +
            (isContainerLink(l)
                ? waitingBelow(l.links)
                : l.suggested > 0
                  ? 1
                  : 0),
        0,
    );

// One count when more than SUMMARY_AFTER wait, a dot for one or two.
const summaryOf = (count: number): OutlineMark =>
    count > SUMMARY_AFTER
        ? { type: "count", count }
        : count > 0
          ? DOT
          : NO_MARK;

// A container's own text waits (not only its parts).
const ownWaits = (link: PageLink): boolean =>
    link.suggested - link.links.reduce((n, l) => n + l.suggested, 0) > 0;

// An open container's row: the count of its waiting parts past
// SUMMARY_AFTER; else a dot only when its own text waits.
const openMarkOf = (link: PageLink): OutlineMark => {
    const parts = waitingParts(link.links);
    if (parts > SUMMARY_AFTER) return { type: "count", count: parts };
    return ownWaits(link) ? DOT : NO_MARK;
};

// The id of the page shown (the root: null).
const shownId = (
    cards: readonly StudioCard[],
    desk: DeskState,
): string | null => {
    const page = studioPage(cards, desk, "", new Map());
    return page.type === "root" ? null : page.id;
};

// A row above the tree: a page about the material.
type AboutRow = {
    readonly id: DeskAbout;
    // The page shown is this row.
    readonly current: boolean;
    // Something on it needs the author (an objective not covered).
    readonly warning: boolean;
    readonly open: readonly DeskCommand[];
};

// The rows above the tree, in order. Sources is reached from the page's
// facts line, not here.
const ABOUT_ROWS: readonly DeskAbout[] = ["info", "objectives"];

// `uncovered`: an objective has no part yet (Learning objectives' dot).
const aboutRows = (
    desk: DeskState,
    uncovered: boolean,
): readonly AboutRow[] => {
    const shown = inFocus(desk);
    return ABOUT_ROWS.map((id) => ({
        id,
        current: shown?.kind === id,
        warning: id === "objectives" && uncovered,
        open: openAboutCommands(id),
    }));
};

// The root first, then each link and (unfolded) its parts. Every container
// starts unfolded; the author's own fold wins (view state, by row key).
const outlineRows = (
    cards: readonly StudioCard[],
    desk: DeskState,
    projectTitle: string,
    folds: ReadonlyMap<string, boolean>,
    waiting: Waiting,
): readonly OutlineRow[] => {
    const root = rootOf(cards);
    const shown = shownId(cards, desk);
    const links = rootLinks(cards, root, waiting);
    // `summarised`: the container holding `ls` counts its waiting parts,
    // so they carry no dot.
    const rows = (
        ls: readonly PageLink[],
        holder: string,
        depth: number,
        summarised: boolean,
    ): readonly OutlineRow[] =>
        ls.flatMap((link): readonly OutlineRow[] => {
            const key = `${holder}/${link.id}`;
            const container = isContainerLink(link);
            const unfolded = folds.get(key) ?? true;
            const fold =
                !container || link.links.length === 0
                    ? "leaf"
                    : unfolded
                      ? "open"
                      : "folded";
            const mark = ((): OutlineMark => {
                switch (fold) {
                    case "open":
                        return openMarkOf(link);
                    case "folded":
                        return summaryOf(
                            waitingBelow(link.links) + (ownWaits(link) ? 1 : 0),
                        );
                    case "leaf":
                        return summarised || link.suggested === 0
                            ? NO_MARK
                            : DOT;
                    default:
                        return exhaustSwitchCase(fold);
                }
            })();
            return [
                {
                    key,
                    id: link.id,
                    title: link.title,
                    kind: link.kind,
                    depth,
                    fold,
                    current: link.id === shown,
                    mark,
                    suggestion: link.suggestion,
                    open: link.open,
                },
                ...(fold === "open"
                    ? rows(
                          link.links,
                          link.id,
                          depth + 1,
                          waitingParts(link.links) > SUMMARY_AFTER,
                      )
                    : []),
            ];
        });
    return [
        {
            key: "root",
            id: root.type === "course" ? root.card.id : "",
            title: projectTitle,
            kind: "root",
            depth: 0,
            fold: "leaf",
            current: shown === null,
            mark:
                waitingParts(links) > SUMMARY_AFTER
                    ? { type: "count", count: waitingParts(links) }
                    : NO_MARK,
            suggestion: false,
            open: rootCommands(root, desk),
        },
        ...rows(links, "root", 1, waitingParts(links) > SUMMARY_AFTER),
    ];
};

// --- The page shown ------------------------------------------------------------

// Back to the root page: the course opened from the desk, or nothing open.
const rootCommands = (
    root: StudioRoot,
    desk: DeskState,
): readonly DeskCommand[] => {
    if (root.type === "course") return openCommands(root.card, undefined);
    const first = desk.path[0];
    return first === undefined ? [] : [{ type: "close", id: first.id }];
};

const titleAt = (cards: readonly StudioCard[], id: string): string =>
    deskBinder(cards, id)?.title ?? deskDocument(cards, id)?.title ?? "";

const studioPage = (
    cards: readonly StudioCard[],
    desk: DeskState,
    projectTitle: string,
    waiting: Waiting,
): StudioPage => {
    const root = rootOf(cards);
    const rootPage: StudioPage = {
        type: "root",
        title: projectTitle,
        container: root.type === "course" ? root.card : undefined,
        links: rootLinks(cards, root, waiting),
    };
    const shown = inFocus(desk);
    if (shown === undefined) return rootPage;
    if (root.type === "course" && shown.id === root.card.id) return rootPage;
    // The levels above the one shown, without the course that is the root.
    const above = desk.path
        .slice(0, -1)
        .filter((e) => root.type !== "course" || e.id !== root.card.id);
    const rootCrumb: PageCrumb = {
        id: root.type === "course" ? root.card.id : "",
        title: projectTitle,
        open: rootCommands(root, desk),
    };
    const crumbs: readonly PageCrumb[] = [
        rootCrumb,
        ...above.map((e) => ({
            id: e.id,
            title: titleAt(cards, e.id),
            open: [{ type: "close_to", id: e.id } satisfies DeskCommand],
        })),
    ];
    switch (shown.kind) {
        case "binder":
        case "shelf": {
            const container = deskBinder(cards, shown.id);
            if (container === undefined) return rootPage;
            const links = partLinks(
                container.parts,
                (id) => openPartCommands(container, id),
                waiting,
                container,
            );
            return {
                type: "container",
                id: container.id,
                title: container.title,
                container,
                crumbs,
                links,
                suggested: sumWaiting(waiting, links),
            };
        }
        case "document": {
            const doc = deskDocument(cards, shown.id);
            if (doc === undefined) return rootPage;
            // The container (or root) it lies in, and its next sibling there.
            const parentEntry = above.at(-1);
            const parent =
                parentEntry === undefined
                    ? undefined
                    : deskBinder(cards, parentEntry.id);
            const siblings: readonly PageLink[] =
                parent === undefined
                    ? rootPage.links
                    : partLinks(
                          parent.parts,
                          (id) => openPartCommands(parent, id),
                          waiting,
                          parent,
                      );
            const at = siblings.findIndex((l) => l.id === doc.id);
            const after = at < 0 ? undefined : siblings[at + 1];
            return {
                type: "document",
                id: doc.id,
                doc,
                kind: kindOfDoc(doc),
                crumbs,
                suggested: waiting.get(doc.id) ?? 0,
                back: crumbs.at(-1),
                next:
                    after === undefined
                        ? undefined
                        : {
                              id: after.id,
                              title: after.title,
                              open: after.open,
                          },
            };
        }
        case "info":
        case "objectives":
        case "sources":
            return { type: "about", id: shown.kind };
        default:
            return exhaustSwitchCase(shown.kind);
    }
};

// Where the page shown sits, for the top bar's breadcrumb (pen Q76: the
// top bar says where you are): the levels above it, each a way back, and
// the page itself, by its title or, for a page about the project, by which
// one it is (its word is the view's).
type TopCrumbs = {
    readonly crumbs: readonly PageCrumb[];
    readonly here:
        | { readonly type: "title"; readonly title: string }
        | { readonly type: "about"; readonly id: DeskAbout };
};

const topCrumbsOf = (
    page: StudioPage,
    projectTitle: string,
    cards: readonly StudioCard[],
    desk: DeskState,
): TopCrumbs => {
    switch (page.type) {
        case "root":
            return { crumbs: [], here: { type: "title", title: page.title } };
        case "container":
            return {
                crumbs: page.crumbs,
                here: { type: "title", title: page.title },
            };
        case "document":
            return {
                crumbs: page.crumbs,
                here: { type: "title", title: page.doc.title },
            };
        case "about":
            return {
                crumbs: [
                    {
                        id: "",
                        title: projectTitle,
                        open: escapeCommands(cards, desk),
                    },
                ],
                here: { type: "about", id: page.id },
            };
        default:
            return exhaustSwitchCase(page);
    }
};

const kindOfDoc = (doc: DeskDocument): PageNodeKind => {
    switch (doc.kind) {
        case "material":
            return isQuiz(doc) ? "quiz" : "page";
        case "source":
            return "source";
        case "other":
            return "file";
        default:
            return exhaustSwitchCase(doc.kind);
    }
};

// Esc (outside a field): one level up; at the root, nothing.
const escapeCommands = (
    cards: readonly StudioCard[],
    desk: DeskState,
): readonly DeskCommand[] => {
    const page = studioPage(cards, desk, "", new Map());
    switch (page.type) {
        case "root":
            return [];
        case "container":
        case "document":
            return page.crumbs.at(-1)?.open ?? [];
        // Back to the root page.
        case "about":
            return [{ type: "close", id: page.id }];
        default:
            return exhaustSwitchCase(page);
    }
};
